import React, {
  useContext,
  useState,
  useRef,
  useEffect,
  useMemo,
  Suspense,
} from "react";
import { useTranslation } from "react-i18next";
import Button from "../../components/ui/button/Button";
import { ActionSidebarsContext } from "../../context/actionSidebarsContext";
import { IActionSidebarsContext } from "../../types/actionSidebars";
import { FleetContext } from "../../context/fleetContext";
import { IFleetContext } from "../../types/fleet";
import { AgGridReact } from "ag-grid-react";
import {
  Vehicle,
  VehicleCategory,
  VehicleComputed,
} from "../../types/vehicles";
import { IRoutesContext, RoutesContext } from "../../context/routesContext";
import dayjs from "dayjs";
import World from "../../assets/data/World";
import CountryMapSvg from "../../components/ui/countryMapSvg/CountryMapSvg";
import { IRoute, IRouteComputed } from "../../types/routes";
import { routesService } from "../../api/routesService/routesService";
import { useLoaderData, useNavigate, useSearchParams } from "react-router-dom";
import PaginationSizeSelector from "../trips/columnDefComponents/PaginationSizeSelector";
import {
  Box,
  FormControlLabel,
  FormGroup,
  IconButton,
  styled,
  Switch,
  SwitchProps,
  Typography,
  Button as MButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import { vehiclesGridData, vehiclesMoke2 } from "./components/vehicles";
import traceabilityIcon from "../../assets/images/icons/traceability.svg";
import {
  AccessTimeRounded,
  Block,
  CheckOutlined,
  DocumentScannerRounded,
} from "@mui/icons-material";
import PopoverComponent from "./components/DialogComponent";
import { VehiclesGrid } from "./components/VehiclesGrid";
import { VehicleData } from "../../types/api";
import DriverCellRenderer from "./components/DriverCellRenderer";
import {
  formatDateGGMMAAAA,
  formatDateIgnoreTimezone,
  formatDateTime,
} from "../../utils/DateUtils";

import SidebarAddDriver from "./components/SidebarAddDriver";
import {
  CheckDriverOnboardingPayload,
  CheckDriverOnboardingResponse,
  fleetService,
} from "../../api/fleetService/fleetServicesRegistered";
import { useBusinessProfile } from "../../utils/hooks/useBusinessProfile";
import { BusinessProfile, UserProfile } from "../../types/profile";
import SidebarAddVehicle from "./components/SidebarAddVehicle";
import { toast } from "react-toastify";
import {
  EmissionsOutput,
  extractEmissionsData,
  extractFuelTypeData,
} from "./components/emissionsUtils";
import SidebarViewVehicle from "./components/SidebarViewVehicle";
import { IOSSwitch } from "./components/IOSSwitch";
import { useAuth } from "../../utils/hooks/useAuth";
import CustomPopover from "./components/CustomPopover";
import { useWindowSize } from "../../utils/hooks/useWindowSize";
import CloseIcon from "@mui/icons-material/Close";
import TextDisplay from "./components/TextDisplay";
import VehicleTypeFilter from "./components/VehicleTypeFilter";
import DriverStatusFilter from "./components/DriverStatusFilter";
import RegisteredVehicleTypeFilter from "./components/RegisteredVehicleTypeFilter";
import RegisteredEnvironmentClassFilter from "./components/RegisteredEnvironmentClassFilter";
import RegisteredFuelTypeFilter from "./components/RegisteredFuelTypeFilter";
import RegisteredVehicleStatusFilter from "./components/RegisteredVehicleStatusFilter";
import DriverPlateFilter from "./components/DriverPlateFilter";
import VehicleLicensePlateFilter from "./components/VehicleLicensePlateFilter";
import AssociationPeriodFilter from "./components/AssociationPeriodFilter";

const FleetGraphs = React.lazy(
  () => import("../../components/fleetGraphs/FleetGraphs")
);
const FleetGraphsRegistered = React.lazy(
  () => import("../../components/fleetGraphs/FleetGraphsRegistered")
);


const FleetAndRoute: React.FC = () => {
  /**DATA INIT */
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const gridRef = useRef<any>(null);
  const onGridReady = (params?: { api: any }) => {
    if (gridRef.current) {
      gridRef.current.api.setDomLayout("autoHeight");
    }
    
    // Initialize the driver plate filter if params are provided
    if (params?.api) {
      params.api.getFilterInstance('associations', (instance: any) => {
        if (instance) {
          instance.setModel(null);
        }
      });
    }
  };

  const onFilterChanged = (event: { api: any }) => {
    const gridApi = event.api;
    const filterModel = gridApi.getFilterModel();

    // Ensure the driver plate filter is properly applied
    if (filterModel.associations) {
      const driverPlateFilter = gridApi.getFilterInstance('associations');
      if (driverPlateFilter) {
        driverPlateFilter.setModel(filterModel.associations);
      }
    }

    gridApi.setFilterModel(filterModel);
  };

  const { businessProfileApi } = useLoaderData() as {
    profileApi: null | UserProfile;
    businessProfileApi: null | BusinessProfile;
  };
  const [businessProfile, setBusinessProfile] =
    useState<BusinessProfile | null>(businessProfileApi || null);
  const {
    drivers: initialDrivers,
    vehiclesList: listaVeicoli,
    fleetInfo,
  } = useLoaderData() as {
    drivers: any[] | null;
    vehiclesList: any;
    fleetInfo: {
      vehicleTypes: Record<string, string[]>;
      envClasses: string[];
      fuels: string[];
    };
  };

  const [driverDataList, setDriverDataList] = useState<any>(
    initialDrivers || []
  );
  const {
    setSidebarModifyFleetOpen,
    setSidebarAddRouteOpen,
    setSidebarAddDriverOpen,
    setSidebarAddVehicleOpen,
  } = useContext(ActionSidebarsContext) as IActionSidebarsContext;

  const [showedTab, setShowedTab] = useState<
    "fleet" | "routes" | "registeredAssets" | "drivers"
  >("registeredAssets");
  const [visualizationType, setVisualizationType] = useState<"grid" | "table">(
    "grid"
  );

  const [vehiclesComputed, setVehiclesComputed] = useState<VehicleCategory[]>(
    []
  );
  const { vehicles, vehiclesInfo, isIntermodal, lastUpdate } = useContext(
    FleetContext
  ) as IFleetContext;

  useEffect(() => {
    setVehiclesComputed(groupVehicles(JSON.parse(JSON.stringify(vehicles))));
  }, [vehicles, vehiclesInfo]);

  useEffect(() => {
    setSidebarModifyFleetOpen(false);
    setSidebarAddRouteOpen(false);
    setSidebarAddDriverOpen(false);
    setSidebarAddVehicleOpen(false);
  }, [showedTab]);

  const { routes, setRoutes } = useContext(RoutesContext) as IRoutesContext;
  const [routesComputed, setRoutesComputed] = useState<IRouteComputed[]>([]);

  useEffect(() => {
    if (routes && routes.routes) {
      const computedRoutes: IRouteComputed[] = [];
      const copyRoutes: IRoute[] = JSON.parse(JSON.stringify(routes.routes));
      copyRoutes.forEach((route) => {
        const specularRouteIndex: number = copyRoutes.findIndex(
          (cR) =>
            cR.countryTo.isoCode === route.countryFrom.isoCode &&
            cR.countryFrom.isoCode === route.countryTo.isoCode &&
            cR.id !== route.id
        );
        if (specularRouteIndex === -1) {
          computedRoutes.push({
            ...route,
            roundtrip: false,
          });
        } else {
          if (
            !computedRoutes.find(
              (cR) =>
                cR.countryTo.isoCode === route.countryFrom.isoCode &&
                cR.countryFrom.isoCode === route.countryTo.isoCode
            )
          ) {
            // Push the roundtrip
            computedRoutes.push({
              ...route,
              roundtrip: true,
            });
          }
        }
      });

      setRoutesComputed(computedRoutes);
    }
  }, [routes]);

  const openSidebarModifyFleet = () => {
    console.log('FleetAndRoute - openSidebarModifyFleet called');
    setSidebarModifyFleetOpen(true);
  };

  const openSidebarAddRoute = () => {
    setSidebarAddRouteOpen(true);
  };

  const filterDropdownRef = useRef<null | HTMLDivElement>(null);
  const [isOpenFilters, setIsOpenFilters] = useState<boolean>(false);
  const [currentFilter, setCurrentFilter] = useState<null | string>(null);
  const openFilters = () => {
    if (!isOpenFilters) {
      setIsOpenFilters(true);
    }
  };

  useEffect(() => {
    const listener = (e: any) => {
      if (
        !!filterDropdownRef.current &&
        !filterDropdownRef.current.contains(e.target) &&
        !isOpenFilters
      ) {
        setIsOpenFilters(false);
      }
    };
    document.addEventListener("click", listener);

    return () => {
      document.removeEventListener("click", listener);
    };
  }, []);

  const changeFilter = (value: null | string) => () => {
    setCurrentFilter(value);
    setIsOpenFilters(false);
  };

  const groupVehicles: (vehicles: Vehicle[]) => VehicleCategory[] = (
    vehicles
  ) => {
    let vehiclesArray: VehicleCategory[] = [];
    vehiclesInfo?.forEach((vehicleInfo) => {
      const vehicleRecord = vehicles.find(
        (v) => v.vehicleId === vehicleInfo.id
      );

      if (!vehiclesArray.find((v) => v.type === vehicleInfo["type"])) {
        vehiclesArray.push({
          type: vehicleInfo["type"],
          vehicles: [
            {
              ...vehicleInfo,
              quantity: !!vehicleRecord ? vehicleRecord.quantity : 0,
              vehicleId: vehicleInfo.id,
              id: !!vehicleRecord ? vehicleRecord.id : null,
            },
          ],
        });
      } else {
        const index = vehiclesArray.findIndex(
          (v) => v.type === vehicleInfo["type"]
        );
        vehiclesArray[index].vehicles.push({
          ...vehicleInfo,
          quantity: !!vehicleRecord ? vehicleRecord.quantity : 0,
          vehicleId: vehicleInfo.id,
          id: !!vehicleRecord ? vehicleRecord.id : null,
        });
      }
    });

    return vehiclesArray;
  };

  const unGroupVehicles = useMemo(() => {
    const vehiclesCopy = [...vehiclesComputed];
    const vehiclesArray: VehicleComputed[] = [];

    vehiclesCopy.forEach((vC) => {
      if (currentFilter === null || vC.type === currentFilter) {
        vC.vehicles.forEach((v) => {
          if (!!v.quantity) {
            vehiclesArray.push(v);
          }
        });
      }
    });

    return vehiclesArray;
  }, [vehiclesComputed, currentFilter]);
  const worldRef = useRef<SVGSVGElement>(null);

  const [isOpenModalDeleteRoute, setIsOpenModalDeleteRoute] =
    useState<boolean>(false);
  const [routeToDelete, setRouteToDelete] = useState<null | IRouteComputed>(
    null
  );
  const [isLoadingDelete, setIsLoadingDelete] = useState<boolean>(false);
  const deleteRoute = (index: number) => () => {
    setRouteToDelete(routesComputed[index]);
    setIsOpenModalDeleteRoute(true);
  };

  const [routesToDelete, setRoutesToDelete] = useState<{ id: string }[]>([]);
  const handleChangeDeleteRouteCheckbox = (id: string) => {
    let array = [...routesToDelete];
    const routeIndex = array.findIndex((r) => r.id === id);

    if (routeIndex >= 0) {
      array.slice(routeIndex, 1);
    } else {
      array.push({ id: id });
    }

    setRoutesToDelete(array);
  };

  const arrayRoutesToDelete = useMemo(() => {
    if (!!routes && !!routes.routes && !!routeToDelete) {
      const array = [];

      const first = routes?.routes.find(
        (r) =>
          r.countryFrom.isoCode === routeToDelete?.countryFrom.isoCode &&
          r.countryTo.isoCode === routeToDelete.countryTo.isoCode
      );
      let second = null;
      if (routeToDelete?.roundtrip) {
        second = routes?.routes.find(
          (r) =>
            r.countryFrom.isoCode === routeToDelete?.countryTo.isoCode &&
            r.countryTo.isoCode === routeToDelete.countryFrom.isoCode &&
            r.id !== first?.id
        );
      }

      if (!!first) {
        array.push(first);
      }

      if (!!second) {
        array.push(second);
      }

      if (array.length === 1) {
        setRoutesToDelete([{ id: array[0].id }]);
      }

      return array;
    }

    return [];
  }, [routeToDelete]);

  const confirmDeleteRoute = async () => {
    if (
      !!routes &&
      !!routes.routes &&
      !!routeToDelete &&
      routesToDelete.length > 0
    ) {
      setIsLoadingDelete(true);

      // Deleting routes request
      const response = await routesService.deleteRoutes(routesToDelete);
      if (response.success) {
        // Reset routes
        const responseGetRoutes = await routesService.getRoutes();
        if (responseGetRoutes.success) {
          setRoutes(responseGetRoutes.responseBody);
          setRouteToDelete(null);
          setIsOpenModalDeleteRoute(false);
        } else {
          window.location.reload();
        }
      }

      setIsLoadingDelete(false);
    }
  };
  const [paginationPageSize, setPaginationPageSize] = useState(50);
  const handlePageSizeChange = (newPageSize: number) => {
    setPaginationPageSize(newPageSize);
  };

  const columnDefs = useMemo(
    () => [
      {
        field: "type",
        headerName: t("screens.fleetAndRoute.tableHeaders.category"),
      },
      {
        field: "name",
        headerName: t("screens.fleetAndRoute.tableHeaders.type"),
      },
      {
        field: "quantity",
        headerName: t("screens.fleetAndRoute.tableHeaders.number"),
      },
    ],
    []
  );

  /**REGISTERED ASSETS AND DRIVER */

  const [selectedVehicle, setSelectedVehicle] = useState<any>(null);
  const [open, setOpen] = useState(false); // Dialog aperto di default
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const handleAddDriver = (newDriver: any) => {
    setDriverDataList((prev: any) => [...prev, newDriver]);
  };

  const handleOpen = (data: {
    type: string;
    name: string;
    vehicles: VehicleData[];
  }) => {
    setSelectedVehicle(data);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedVehicle(null);

  };
  const capitalizeFirstLetter = (text: string): string => {
    if (!text) return "";
    const lowerCased = text.toLowerCase();
    return lowerCased.charAt(0).toUpperCase() + lowerCased.slice(1);
  };

  useEffect(() => { }, [selectedVehicle]);
  
 

  const [isSidebarViewOpen, setIsSidebarViewOpen] = useState(false);
  const handleOpenViewSidebar = (vehicle: Vehicle) => {
    setSelectedVehicle(vehicle);
    setIsSidebarViewOpen(true);
  };

  const handleCloseViewSidebar = () => {
    setIsSidebarViewOpen(false);
    setSelectedVehicle(null);
  };
  const handleCloseViewSidebarAddDriver = () => {
    setIsSidebarOpen(false);
  };
  const [isSidebarAddVehicleOpen, setIsSidebarAddVehicleOpen] = useState(false);

  const handleOpenSidebarAddVehicle = (vehicle?: any) => {
    setIsSidebarViewOpen(false);
    setIsSidebarAddVehicleOpen(true);
  };
  const handleCloseSidebarAddVehicle = () => {
    setIsSidebarAddVehicleOpen(false);
    setSelectedVehicle(null);
    // setOpen(false); // RIMOSSO: il DialogComponent deve restare aperto
  };

  const handleAddVehicle = (
    vehicle: any,
    isEdit: boolean,
    originalLicensePlate: string
  ) => {
    setVehiclesList((prevVehicles: any) => {
      if (isEdit) {
        return prevVehicles.map((v: any) =>
          v.licensePlate === originalLicensePlate ? vehicle : v
        );
      } else {
        return [...prevVehicles, vehicle];
      }
    });
    handleCloseSidebarAddVehicle();
  };

  const [vehiclesList, setVehiclesList] = useState<Array<any>>(
    Array.isArray(listaVeicoli) ? listaVeicoli : []
  );

  const columnDefsRegistered = useMemo(
    () => [
      {
        headerName: t("screens.fleetAndRoute.gridAssets.plate"),
        field: "licensePlate",
        headerTooltip: t("screens.fleetAndRoute.gridAssets.plate"),
        tooltipValueGetter: (params: any) => params.data.licensePlate,
        filter: "agTextColumnFilter",
        filterParams: {
          filterOptions: ['contains'],
          buttons: [],
          closeOnApply: false,
          suppressAndOrCondition: true,
        },
        floatingFilter: true,
        cellRenderer: (params: any) => {
          return (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "flex-start",
                alignItems: "center",
                height: "40px",
                textDecoration: "underline",
              }}
            >
              <MButton
                onClick={() => handleOpenViewSidebar(params.data)}
                variant="text"
                sx={{
                  color: "#333333",
                  padding: 0,
                  margin: 0,
                  fontSize: "14px",
                }}
              >
                {params.data.licensePlate}
              </MButton>
            </Box>
          );
        },
      },
      {
        headerName: t("screens.fleetAndRoute.gridAssets.vehicle"),
        field: "category",
        valueGetter: (params: any) => {
          if (!params.data) return "";
          const mainCategoryKey = params.data.mainCategory?.trim();
          const categoryKey = params.data.category?.trim();
          const mainCategoryParsed = t(
            `screens.fleetAndRoute.vehicles.${mainCategoryKey}`,
            mainCategoryKey
          ).toString();
          const categoryParsed = t(
            `screens.fleetAndRoute.vehicles.${categoryKey}`,
            categoryKey
          ).toString();
          return `${capitalizeFirstLetter(mainCategoryParsed)} - ${categoryParsed}`;
        },
        cellRenderer: (params: any) => {
          return (<Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "flex-start",
              alignItems: "center",
              height: "40px",
            }}
          > {params.value}</Box>)
        },
        tooltipValueGetter: (params: any): string => {
          if (!params.data) return "";
          const mainCategoryKey = params.data.mainCategory?.trim();
          const categoryKey = params.data.category?.trim();
          const mainCategoryParsed = t(
            `screens.fleetAndRoute.vehicles.${mainCategoryKey}`,
            mainCategoryKey
          ).toString();
          const categoryParsed = t(
            `screens.fleetAndRoute.vehicles.${categoryKey}`,
            categoryKey
          ).toString();
          return `${capitalizeFirstLetter(mainCategoryParsed)} - ${categoryParsed}`;
        },
        headerTooltip: t("screens.fleetAndRoute.gridAssets.vehicle"),
        filter: RegisteredVehicleTypeFilter,
      },
      {
        headerName: t("screens.fleetAndRoute.gridAssets.firstRegistrationYear"),
        field: "firstRegistrationDate",
        filter: "agDateColumnFilter",
        valueGetter: (params: any) => {
          if (!params.data?.firstRegistrationDate) return null;
          const date = new Date(params.data.firstRegistrationDate);
          return isNaN(date.getTime()) ? null : date;
        },
        filterParams: {
          maxNumConditions: 1,
          filterOptions: ['inRange'],
          comparator: (filterLocalDateAtMidnight: Date, cellValue: Date) => {
            if (!cellValue) return -1;
            const startOfDay = new Date(filterLocalDateAtMidnight);
            const endOfDay = new Date(filterLocalDateAtMidnight);
            endOfDay.setHours(23, 59, 59, 999);

            if (cellValue >= startOfDay && cellValue <= endOfDay) {
              return 0;
            }
            return cellValue < startOfDay ? -1 : 1;
          },
        },
        cellRenderer: (params: any) => {
          const date = params.value;
          if (!date) return <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "flex-start", alignItems: "center", height: "40px" }}>-</Box>;
          return <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "flex-start", alignItems: "center", height: "40px" }}>{formatDateGGMMAAAA(date)}</Box>;
        },
      },

      {
        headerName: t("screens.fleetAndRoute.gridAssets.enviromentClass"),
        field: "environmentClass",
        headerTooltip: t("screens.fleetAndRoute.gridAssets.enviromentClass"),
        tooltipField: "environmentClass",
        tooltipValueGetter: (params: any): string => {
          const environmentClass = params.data?.environmentClass;
          if (!environmentClass) return "-";
          return environmentClass;
        },
        cellRenderer: (params: any) => {
          const environmentClass = params.data?.environmentClass;
          if (!environmentClass) return <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "flex-start", alignItems: "center", height: "40px" }}>-</Box>;
          return <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "flex-start", alignItems: "center", height: "40px" }}>{environmentClass}</Box>;
        },
        filter: RegisteredEnvironmentClassFilter,
      },
      {
        headerName: t("screens.fleetAndRoute.gridAssets.fuelType"),
        field: "fuelType",
        valueGetter: (params: any): string => {
          if (!params.data) return "-";
          const fuelTypeKey = params.data.fuelType?.trim() || "-";
          return t(`screens.fleetAndRoute.fuelType.${fuelTypeKey}`, fuelTypeKey).toString();
        },
        cellRenderer: (params: any) => {
          const value = params.value || "-";
          return (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "flex-start",
                alignItems: "center",
                height: "40px",
              }}
            >
              <Typography sx={{ color: "#333333", fontSize: "14px" }}>
                {value}
              </Typography>
            </Box>
          );
        },
        tooltipValueGetter: (params: any): string => {
          const value = params.value || "-";
          return value;
        },
        headerTooltip: t("screens.fleetAndRoute.gridAssets.fuelType"),
        filter: RegisteredFuelTypeFilter,
      },
      {
        headerName: t("screens.fleetAndRoute.gridAssets.emissionsData"),
        field: "emissionsData",
        filter: "agTextColumnFilter",
        filterParams: {
          filterOptions: ['contains'],
          buttons: [],
          closeOnApply: false,
          suppressAndOrCondition: true,
        },
        floatingFilter: true,
        headerTooltip: t("screens.fleetAndRoute.gridAssets.emissionsData"),
        tooltipField: "emissionsData",

        // Tooltip con valore formattato
        tooltipValueGetter: (params: any): string => {
          const emissionsData = params.data?.emissionsData;
          if (emissionsData == null || emissionsData === "") return "-"; // Gestisce null o vuoto
          return `${emissionsData} g CO2/km`; // Aggiunge l'unità di misura nel tooltip
        },

        // CellRenderer con controllo per evitare di mostrare "g CO2/km" se il valore è null o vuoto
        cellRenderer: (params: any) => {
          const emissionsData = params.data?.emissionsData;
          if (emissionsData == null || emissionsData === "") return "-"; // Gestisce null o vuoto
          return (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "flex-start",
                alignItems: "center",
                height: "40px",
              }}
            >
              <Typography sx={{ color: "#333333", fontSize: "14px" }}>
                {emissionsData} g CO2/km
              </Typography>
            </Box>
          );
        },
      }
      ,
      {
        headerName: t("screens.fleetAndRoute.gridAssets.status"),
        headerTooltip: t("screens.fleetAndRoute.gridAssets.status").toString(),
        field: "status",
        tooltipValueGetter: (params: any): string => {
          return params.data?.status === "ACTIVE"
            ? t("screens.fleetAndRoute.status.ACTIVE")
            : t("screens.fleetAndRoute.status.CANCELLED");
        },
        valueFormatter: (params: any) => {
          if (params.value === "ACTIVE") return t("screens.fleetAndRoute.status.ACTIVE");
          if (params.value === "CANCELLED") return t("screens.fleetAndRoute.status.CANCELLED");
          return params.value;
        },
        filterValueGetter: (params: any) => {
          if (params.data?.status === "ACTIVE") return t("screens.fleetAndRoute.status.ACTIVE");
          if (params.data?.status === "CANCELLED") return t("screens.fleetAndRoute.status.CANCELLED");
          return params.data?.status;
        },
        filter: RegisteredVehicleStatusFilter,
        cellRenderer: (params: any) => {
          return (
            <FormGroup
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <FormControlLabel
                control={
                  <IOSSwitch
                    id={`target-element-${params.data.licensePlate}`}
                    sx={{ m: 1 }}
                    checked={params.data.status === "ACTIVE"}
                    onChange={() => handleOpenDialog(params.data.licensePlate)}
                  />
                }
                label=""
              />
            </FormGroup>
          );
        },
      }
      ,

      {
        headerName: t("screens.fleetAndRoute.gridAssets.lastTraceabilityCheck"),
        field: "lastTraceabilityCheck",
        minWidth: 250,
        filter: "agDateColumnFilter",

        // Tooltip per l'intestazione (header)
        headerTooltip: t("screens.fleetAndRoute.gridAssets.lastTraceabilityCheck").toString(),

        // Tooltip per il valore della cella
        tooltipValueGetter: (params: any): string => {
          const lastTraceabilityCheck = params.data?.lastTraceabilityCheck;
          if (!lastTraceabilityCheck) return "-"; // Gestisce null o vuoto

          // Formatta la data come nel cellRenderer
          const formattedDate = formatDateTime(lastTraceabilityCheck);
          return formattedDate;
        },
        // Convertiamo il valore in una Date per il filtro
        valueGetter: (params: any) => {
          if (!params.data?.lastTraceabilityCheck) return null;
          const date = new Date(params.data.lastTraceabilityCheck);
          return isNaN(date.getTime()) ? null : date;
        },

        filterParams: {
          maxNumConditions: 1,
          filterOptions: ['inRange'],
          comparator: (filterLocalDateAtMidnight: Date, cellValue: Date) => {
            if (!cellValue) return -1;
            const startOfDay = new Date(filterLocalDateAtMidnight);
            const endOfDay = new Date(filterLocalDateAtMidnight);
            endOfDay.setHours(23, 59, 59, 999);

            if (cellValue >= startOfDay && cellValue <= endOfDay) {
              return 0;
            }
            return cellValue < startOfDay ? -1 : 1;
          },
        },

        cellRenderer: (params: any) => {
          if (!params.data.lastTraceabilityCheck) return <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "flex-start", alignItems: "center", height: "40px" }}>-</Box>; // Gestione di valori null o undefined

          // Formatta la data
          const formattedDate = formatDateTime(params.data.lastTraceabilityCheck);

          return (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "flex-start",
                alignItems: "center",
                height: "40px",
              }}
            >
              {params.data.traceability === true ? (
                <CheckOutlined
                  sx={{ color: "green", margin: "5px 8px 5px 8px" }}
                />
              ) : (
                <Block sx={{ color: "#CF0A2C", margin: 1 }} />
              )}

              <Typography
                sx={{
                  padding: 0,
                  marginTop: "2px",
                  color: "#333333",
                  fontSize: "14px",
                }}
              >
                {formattedDate}
              </Typography>
            </Box>
          );
        },
      }

    ],
    []
  );

  const [performResult, setPerformResult] =
    useState<CheckDriverOnboardingResponse | null>(null);
  const [errorPerform, setErrorPerform] = useState<string | null>(null);
  const [loadingRows, setLoadingRows] = useState<{ [key: string]: boolean }>({});

  const handleCheckOnboarding = async (driverPhone: string) => {
    if (loadingRows[driverPhone]) return;

    setLoadingRows((prev) => ({ ...prev, [driverPhone]: true }));
    setErrorPerform(null);

    try {
      const payload: CheckDriverOnboardingPayload = {
        legacyCodes: {
          legacyCode: businessProfile?.legacycodes[0].legacycode ?? "",
          company: businessProfile?.legacycodes[0].company ?? "",
        },
        phoneNumber: driverPhone,
      };

      const response = await fleetService.checkDriverOnboarding(payload);
      setPerformResult(response);
      // Aggiorno il timestamp di onboardingDateVerify
      setDriverDataList((prevList: any) =>
        prevList.map((driver: any) =>
          driver.phone === driverPhone
            ? { ...driver, onboardingDateVerify: new Date().toISOString() }
            : driver
        )
      );

      // Toast di successo
      if (response.result === "OK") {
        toast.success("Verifica onboarding effettuata: Verificato");
      } else {
        toast.warning("Verifica onboarding effettuata: Non Verificato");
      }
    } catch (err) {
      setErrorPerform("Errore nel controllo dell'onboarding");

      // Toast di errore
      toast.error("Errore durante la verifica dell'onboarding.");
    } finally {
      setLoadingRows((prev) => ({ ...prev, [driverPhone]: false }));
    }
  };

  const [loadingOnboardingRequest, setLoadingOnboardingRequest] =
    useState(false);
  const [errorOnboardingRequest, setErrorOnboardingRequest] = useState<
    string | null
  >(null);

  const handleRequestOnboarding = async (driverPhone: string) => {
    setLoadingOnboardingRequest(true);
    setErrorOnboardingRequest(null);

    const payload: CheckDriverOnboardingPayload = {
      legacyCodes: {
        legacyCode: businessProfile?.legacycodes[0].legacycode ?? "",
        company: businessProfile?.legacycodes[0].company ?? "",
      },
      phoneNumber: driverPhone,
    };

    try {
      const response = await fleetService.requestDriverOnboarding(payload);

      // Aggiorno lo stato `onboardingStatus` del driver corrispondente
      setDriverDataList((prevList: any) =>
        prevList.map((driver: any) =>
          driver.phone === driverPhone
            ? { ...driver, onboardingStatus: "Ongoing" }
            : driver
        )
      );
      toast.success("Richiesta di onboarding inviata con successo!");
    } catch (err) {
      setErrorOnboardingRequest("Errore durante la richiesta di onboarding.");
      console.error(err);
      toast.error("Errore durante la richiesta di onboarding.");
    } finally {
      setLoadingOnboardingRequest(false);
    }

  };

  const columnDefsDrivers = useMemo(
    () => [
      {
        headerName: t("screens.fleetAndRoute.gridDrivers.driver"),
        field: "driver",
        minWidth: 200,
        cellRenderer: (params: any) => {
          return (
            <DriverCellRenderer
              data={params.data}
              driverDataList={driverDataList}
              setDriverDataList={setDriverDataList}
              vehiclesList={vehiclesList}
              onOpenDialogDriverStatus={handleOpneDialogDriverStatus}
            />
          );
        },
                 valueGetter: (params: any): string => {
           const firstName = params.data?.firstName || "";
           const lastName = params.data?.lastName || "";
           return `${firstName} ${lastName}`;
         },
        sortable: true,
        filter: "agTextColumnFilter",
        filterParams: {
          filterOptions: ['contains'],
          buttons: [],
          closeOnApply: false,
          suppressAndOrCondition: true,
        },
        floatingFilter: true,

        // Tooltip per l'intestazione
        headerTooltip: t("screens.fleetAndRoute.gridDrivers.driver"),

        // Tooltip per il valore della cella
        tooltipValueGetter: (params: any): string => {
          const driverName = params.data?.firstName + " " + params.data?.lastName;
          if (!driverName) return "-";
          return driverName;
        },
      },
      {
        headerName: t("screens.fleetAndRoute.gridDrivers.phone"),
        field: "phone",
        sortable: true,
        filter: "agTextColumnFilter",
        filterParams: {
          filterOptions: ['contains'],
          buttons: [],
          closeOnApply: false,
          suppressAndOrCondition: true,
        },
        floatingFilter: true,
        minWidth: 120,

        headerTooltip: t("screens.fleetAndRoute.gridDrivers.phone"),

        tooltipValueGetter: (params: any): string => {
          const phoneNumber = params.data?.phone;

          if (!phoneNumber || phoneNumber.trim() === "") {
            return "Numero di telefono non disponibile";
          }

          return phoneNumber;
        },
        cellRenderer: (params: any) => {
          const phoneNumber = params.data?.phone;
          if (!phoneNumber) return <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "flex-start", alignItems: "center", height: "40px" }}>-</Box>;
          return <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "flex-start", alignItems: "center", height: "40px" }}>{phoneNumber}</Box>;
        },
      },
      {
        headerName: t("screens.fleetAndRoute.gridDrivers.email"),
        field: "email",
        sortable: true,
        filter: "agTextColumnFilter",
        filterParams: {
          filterOptions: ['contains'],
          buttons: [],
          closeOnApply: false,
          suppressAndOrCondition: true,
        },
        floatingFilter: true,
        minWidth: 220,
        headerTooltip: t("screens.fleetAndRoute.gridDrivers.email"),
        tooltipValueGetter: (params: any): string => {
          const email = params.data?.email;
          if (!email || email.trim() === "") {
            return "Email non disponibile";
          }
          return email;
        },
        cellRenderer: (params: any) => {
          const email = params.data?.email;
          if (!email) return <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "flex-start", alignItems: "center", height: "40px" }}>-</Box>;
          return <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "flex-start", alignItems: "center", height: "40px" }}>{email}</Box>;
        },
      },
      {
        headerName: t("screens.fleetAndRoute.gridDrivers.plate"),
        field: "associations",
        filter: 'DriverPlateFilter',
        filterParams: {
          buttons: [],
          closeOnApply: false,
          suppressAndOrCondition: true,
        },
        floatingFilter: true,
        cellRenderer: (params: any) => {
          const today = new Date();

          const activeAssociations = params.data.associations?.filter((a: any) => a.status === "ACTIVE") || [];

          const todayIncluded = activeAssociations.find((a: any) => {
            const from = new Date(a.dateFrom);
            // Se dateTo è null, significa periodo infinito (dal dateFrom in poi)
            if (a.dateTo === null || a.dateTo === undefined) {
              return from <= today;
            }
            const to = new Date(a.dateTo);
            return from <= today && to >= today;
          });

          let selectedAssociation = todayIncluded;

          if (!selectedAssociation) {
            selectedAssociation = activeAssociations
              .filter((a: any) => new Date(a.dateFrom) > today)
              .sort((a: any, b: any) =>
                new Date(a.dateFrom).getTime() - new Date(b.dateFrom).getTime()
              )[0];
          }

          if (!selectedAssociation) return <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "flex-start", alignItems: "center", height: "40px" }}>-</Box>;

          const plate = selectedAssociation?.vehicleLicensePlate;
          if (!plate) return <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "flex-start", alignItems: "center", height: "40px" }}>-</Box>;
          return <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "flex-start", alignItems: "center", height: "40px" }}>{plate}</Box>;
        },
        sortable: true,
        minWidth: 140,
        headerTooltip: t("screens.fleetAndRoute.gridDrivers.plate"),
        tooltipValueGetter: (params: any): string => {
          const today = new Date();
          const activeAssociations = params.data.associations?.filter((a: any) => a.status === "ACTIVE") || [];
          
          const todayIncluded = activeAssociations.find((a: any) => {
            const from = new Date(a.dateFrom);
            // Se dateTo è null, significa periodo infinito (dal dateFrom in poi)
            if (a.dateTo === null || a.dateTo === undefined) {
              return from <= today;
            }
            const to = new Date(a.dateTo);
            return from <= today && to >= today;
          });

          let selectedAssociation = todayIncluded;

          if (!selectedAssociation) {
            selectedAssociation = activeAssociations
              .filter((a: any) => new Date(a.dateFrom) > today)
              .sort((a: any, b: any) =>
                new Date(a.dateFrom).getTime() - new Date(b.dateFrom).getTime()
              )[0];
          }

          const plate = selectedAssociation?.vehicleLicensePlate;
          if (!plate || plate.trim() === "") {
            return "Targa non disponibile";
          }
          return plate;
        },
      },
      {
        headerName: t("screens.fleetAndRoute.gridDrivers.vehicleType"),
        field: "associations",
        valueGetter: (params: any) => {
          const today = new Date();
          const activeAssociations = params.data.associations?.filter((a: any) => a.status === "ACTIVE") || [];
          const todayIncluded = activeAssociations.find((a: any) => {
            const from = new Date(a.dateFrom);
            // Se dateTo è null, significa periodo infinito (dal dateFrom in poi)
            if (a.dateTo === null || a.dateTo === undefined) {
              return from <= today;
            }
            const to = new Date(a.dateTo);
            return from <= today && to >= today;
          });
          let selectedAssociation = todayIncluded;
          if (!selectedAssociation) {
            selectedAssociation = activeAssociations
              .filter((a: any) => new Date(a.dateFrom) > today)
              .sort((a: any, b: any) =>
                new Date(a.dateFrom).getTime() - new Date(b.dateFrom).getTime()
              )[0];
          }
          if (!selectedAssociation) return "-";
          const vehicleTypeKey = selectedAssociation?.vehicleType?.trim() || "";
          const vehicleSubTypeKey = selectedAssociation?.vehicleSubType?.trim() || "";
          const vehicleType = t(
            `screens.fleetAndRoute.vehicles.${vehicleTypeKey}`,
            vehicleTypeKey
          );
          const vehicleSubType = t(
            `screens.fleetAndRoute.vehicles.${vehicleSubTypeKey}`,
            vehicleSubTypeKey
          );
          return `${vehicleType} - ${vehicleSubType}`;
        },
        cellRenderer: (params: any) => {
          return <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "flex-start", alignItems: "center", height: "40px" }}>{params.value}</Box>;
        },
        sortable: true,
        filter: VehicleTypeFilter,
        minWidth: 180,
        headerTooltip: t("screens.fleetAndRoute.gridDrivers.vehicleType"),
        tooltipValueGetter: (params: any): string => {
          const today = new Date();
          const activeAssociations = params.data.associations?.filter((a: any) => a.status === "ACTIVE") || [];
          
          const todayIncluded = activeAssociations.find((a: any) => {
            const from = new Date(a.dateFrom);
            // Se dateTo è null, significa periodo infinito (dal dateFrom in poi)
            if (a.dateTo === null || a.dateTo === undefined) {
              return from <= today;
            }
            const to = new Date(a.dateTo);
            return from <= today && to >= today;
          });

          let selectedAssociation = todayIncluded;

          if (!selectedAssociation) {
            selectedAssociation = activeAssociations
              .filter((a: any) => new Date(a.dateFrom) > today)
              .sort((a: any, b: any) =>
                new Date(a.dateFrom).getTime() - new Date(b.dateFrom).getTime()
              )[0];
          }

          if (!selectedAssociation) return "-";

          const vehicleTypeKey = selectedAssociation?.vehicleType?.trim() || "";
          const vehicleSubTypeKey = selectedAssociation?.vehicleSubType?.trim() || "";
          const vehicleType = t(
            `screens.fleetAndRoute.vehicles.${vehicleTypeKey}`,
            vehicleTypeKey
          );
          const vehicleSubType = t(
            `screens.fleetAndRoute.vehicles.${vehicleSubTypeKey}`,
            vehicleSubTypeKey
          );
          return `${vehicleType} - ${vehicleSubType}`;
        },
      },
      {
        headerName: t("screens.fleetAndRoute.gridDrivers.associationPeriod"),
        field: "associations",
        minWidth: 270,

        cellRenderer: (params: any) => {
          const today = new Date();

          const activeAssociations = params.data.associations?.filter((a: any) => a.status === "ACTIVE") || [];

          const todayIncluded = activeAssociations.find((a: any) => {
            const from = new Date(a.dateFrom);
            // Se dateTo è null, significa periodo infinito (dal dateFrom in poi)
            if (a.dateTo === null || a.dateTo === undefined) {
              return from <= today;
            }
            const to = new Date(a.dateTo);
            return from <= today && to >= today;
          });

          let selectedAssociation = todayIncluded;

          if (!selectedAssociation) {
            selectedAssociation = activeAssociations
              .filter((a: any) => new Date(a.dateFrom) > today)
              .sort((a: any, b: any) =>
                new Date(a.dateFrom).getTime() - new Date(b.dateFrom).getTime()
              )[0];
          }

          if (!selectedAssociation) return <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "flex-start", alignItems: "center", height: "40px" }}>-</Box>;

          const parsedDateFrom = new Date(selectedAssociation.dateFrom).toLocaleString(undefined, {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
          }).replace(",", "");

          // Gestione del dateTo quando è null
          const parsedDateTo = selectedAssociation.dateTo === null || selectedAssociation.dateTo === undefined 
            ? "..." 
            : new Date(selectedAssociation.dateTo).toLocaleString(undefined, {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
              }).replace(",", "");

          return <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "flex-start", alignItems: "center", height: "40px" }}>{`${parsedDateFrom} - ${parsedDateTo}`}</Box>;
        },

        sortable: true,
        filter: 'agCustomFilter',
        filterParams: {
          filter: AssociationPeriodFilter,
          buttons: [],
          closeOnApply: false,
          suppressAndOrCondition: true,
        },
        floatingFilter: true,

        headerTooltip: t("screens.fleetAndRoute.gridDrivers.associationPeriod").toString(),

        tooltipValueGetter: (params: any): string => {
          const today = new Date();

          const activeAssociations = params.data.associations?.filter((a: any) => a.status === "ACTIVE") || [];

          const todayIncluded = activeAssociations.find((a: any) => {
            const from = new Date(a.dateFrom);
            // Se dateTo è null, significa periodo infinito (dal dateFrom in poi)
            if (a.dateTo === null || a.dateTo === undefined) {
              return from <= today;
            }
            const to = new Date(a.dateTo);
            return from <= today && to >= today;
          });

          let selectedAssociation = todayIncluded;

          if (!selectedAssociation) {
            selectedAssociation = activeAssociations
              .filter((a: any) => new Date(a.dateFrom) > today)
              .sort((a: any, b: any) =>
                new Date(a.dateFrom).getTime() - new Date(b.dateFrom).getTime()
              )[0];
          }

          if (!selectedAssociation) return "-";

          const parsedDateFrom = new Date(selectedAssociation.dateFrom).toLocaleString(undefined, {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
          }).replace(",", "");

          // Gestione del dateTo quando è null
          const parsedDateTo = selectedAssociation.dateTo === null || selectedAssociation.dateTo === undefined 
            ? "..." 
            : new Date(selectedAssociation.dateTo).toLocaleString(undefined, {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
              }).replace(",", "");

          return `${parsedDateFrom} - ${parsedDateTo}`;
        },
      },
      {
        headerName: t("screens.fleetAndRoute.gridDrivers.driverStatus"),
        headerTooltip: t("screens.fleetAndRoute.gridDrivers.status").toString(),
        field: "status",
        minWidth: 140,
        tooltipValueGetter: (params: any): string => {
          return params.data?.status === "ACTIVE"
            ? t("screens.fleetAndRoute.status.ACTIVE")
            : t("screens.fleetAndRoute.status.CANCELLED");
        },
        valueFormatter: (params: any) => {
          if (params.value === "ACTIVE") return t("screens.fleetAndRoute.status.ACTIVE");
          if (params.value === "CANCELLED") return t("screens.fleetAndRoute.status.CANCELLED");
          return params.value;
        },
        filterValueGetter: (params: any) => {
          if (params.data?.status === "ACTIVE") return t("screens.fleetAndRoute.status.ACTIVE");
          if (params.data?.status === "CANCELLED") return t("screens.fleetAndRoute.status.CANCELLED");
          return params.data?.status;
        },
        filter: DriverStatusFilter,
        cellRenderer: (params: any) => {
          return (
            <FormGroup
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <FormControlLabel
                control={
                  <IOSSwitch
                    id={`target-element-${params.data.phone}`}
                    sx={{ m: 1 }}
                    checked={params.data.status === "ACTIVE"}
                    onChange={() =>
                      handleOpneDialogDriverStatus(params.data.phone)
                    }
                  />
                }
                label=""
              />
            </FormGroup>
          );
        },
        sortable: true,
      },
      {
        minWidth: 180,
        headerName: t("screens.fleetAndRoute.gridDrivers.lastLogin"),
        field: "lastAccess",
        sortable: true,
        filter: "agDateColumnFilter",
        valueGetter: (params: any) => {
          if (!params.data?.lastAccess) return null;
          const date = new Date(params.data.lastAccess);
          return isNaN(date.getTime()) ? null : date;
        },

        filterParams: {
          browserDatePicker: true, // Abilita il date picker
          comparator: (filterLocalDateAtMidnight: Date, cellValue: Date) => {
            if (!cellValue) return -1;

            // Normalizziamo le date per ignorare l'orario
            const cellDate = new Date(cellValue);
            const filterDate = new Date(filterLocalDateAtMidnight);

            cellDate.setHours(0, 0, 0, 0);
            filterDate.setHours(0, 0, 0, 0);

            if (cellDate < filterDate) return -1;
            if (cellDate > filterDate) return 1;
            return 0; // Ora il filtro "equals" funziona correttamente
          },
        },
        cellRenderer: (params: any) => {
          const { value } = params;

          // Se la data non esiste o è invalida, mostra "-"
          if (!value || isNaN(new Date(value).getTime())) {
            return <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "flex-start", alignItems: "center", height: "40px" }}>-</Box>;
          }

          return <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "flex-start", alignItems: "center", height: "40px" }}>{formatDateTime(value)}</Box>;
        },
        headerTooltip: t("screens.fleetAndRoute.gridDrivers.lastLogin").toString(),
        tooltipValueGetter: (params: any): string => {
          const { value } = params;

          // Se la data non esiste o è invalida, restituisce "-"
          if (!value || isNaN(new Date(value).getTime())) {
            return "-";
          }

          return formatDateTime(value);
        },
      },
      {
        headerName: t("screens.fleetAndRoute.gridDrivers.appOnboardingRequest"),
        field: "onboardingDateVerify",
        minWidth: 210,
        filter: "agDateColumnFilter",
        valueGetter: (params: any) => {
          if (!params.data?.onboardingDateVerify) return null;
          const date = new Date(params.data.onboardingDateVerify);
          return isNaN(date.getTime()) ? null : date;
        },

        filterParams: {
          browserDatePicker: true, // Abilita il date picker
          comparator: (filterLocalDateAtMidnight: Date, cellValue: Date) => {
            if (!cellValue) return -1;

            // Normalizziamo le date per ignorare l'orario
            const cellDate = new Date(cellValue);
            const filterDate = new Date(filterLocalDateAtMidnight);

            cellDate.setHours(0, 0, 0, 0);
            filterDate.setHours(0, 0, 0, 0);

            if (cellDate < filterDate) return -1;
            if (cellDate > filterDate) return 1;
            return 0; // Ora il filtro "equals" funziona correttamente
          },
        },

        cellRenderer: (params: any) => {
          const { onboardingDateVerify, onboardingStatus } = params.data;

          // Se la data non esiste o è invalida, mostra "-"
          if (!onboardingDateVerify || isNaN(new Date(onboardingDateVerify).getTime())) {
            return <span>-</span>;
          }

          // Formatta la data
          const formattedDate = formatDateTime(onboardingDateVerify);

          return (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "flex-start",
                alignItems: "center",
                height: "40px",
              }}
            >
              {onboardingStatus === "Verified" ? (
                <CheckOutlined
                  sx={{
                    color: "green",
                    padding: 0,
                    margin: 0,
                    marginRight: "8px",
                  }}
                />
              ) : (
                <Block
                  sx={{
                    color: "#CF0A2C",
                    padding: 0,
                    margin: 0,
                    marginRight: "8px",
                  }}
                />
              )}

              <Typography sx={{ padding: 0, marginTop: "2px", fontSize: "14px" }}>
                {formattedDate}
              </Typography>
            </Box>
          );
        },
        headerTooltip: t("screens.fleetAndRoute.gridDrivers.appOnboardingRequest").toString(),
        tooltipValueGetter: (params: any): string => {
          const { onboardingDateVerify } = params.data;

          // Se la data non esiste o è invalida, restituisce "-"
          if (!onboardingDateVerify || isNaN(new Date(onboardingDateVerify).getTime())) {
            return "-";
          }

          return formatDateTime(onboardingDateVerify);
        },
      },
      {
        field: "Perform",
        headerName: t("screens.fleetAndRoute.gridDrivers.perform"),
        minWidth: 165,
        filter: false,
        suppressRowClickSelection: true,
        suppressCellFocus: true,
        suppressMovableColumns: true,
        headerTooltip: t("screens.fleetAndRoute.gridDrivers.perform").toString(),
        tooltipValueGetter: (params: any): string => {
          return t("screens.fleetAndRoute.gridDrivers.perform").toString();
        },
        cellRenderer: (params: any) => {
          return (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {loadingRows[params.data.phone] ? (
                <CircularProgress
                  size={24}
                  sx={{ marginTop: "8px", color: "#CF0A2C" }}
                />
              ) : (
                <IconButton
                  onClick={() => handleCheckOnboarding(params.data.phone)}
                >
                  <img src={traceabilityIcon} alt="traceabilityIcon" />
                </IconButton>
              )}
            </Box>
          );
        },
      },
      {
        headerName: t("screens.fleetAndRoute.gridDrivers.onboardingStatus"),
        field: "onboardingStatus",
        sortable: true,
        filter: false,
        suppressRowClickSelection: true,
        suppressCellFocus: true,
        suppressMovableColumns: true,
        minWidth: 200,
        headerTooltip: t("screens.fleetAndRoute.gridDrivers.onboardingStatus").toString(),
        tooltipValueGetter: (params: any): string => {
          if (params.value === "Verified") return t("screens.fleetAndRoute.statusOnboarding.verified");
          if (params.value === "Ongoing") return t("screens.fleetAndRoute.statusOnboarding.ongoing");
          if (params.value === "Request") return t("screens.fleetAndRoute.statusOnboarding.request");

          return "-";
        },
        cellRenderer: (params: any) => {
          if (params.value === "Verified") return null;
          if (params.value === "Ongoing") {
            return (
              <Box
                sx={{
                  width: "100%",
                  height: "39px",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <MButton
                  fullWidth
                  variant="contained"
                  disabled
                  color="secondary"
                  endIcon={<AccessTimeRounded />}
                  sx={{
                    maxHeight: "30px",
                    paddingLeft: "10px",
                    paddingRight: "10px",
                  }}
                >
                  {t("screens.fleetAndRoute.headerDriver.onGoing")}
                </MButton>
              </Box>
            );
          }
          if (params.value === "Request") {
            return (
              <Box
                sx={{
                  width: "100%",
                  height: "39px",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {" "}
                <MButton
                  onClick={() => handleRequestOnboarding(params.data.phone)}
                  fullWidth
                  variant="contained"
                  sx={{
                    backgroundColor: "#CF0A2C",
                    maxHeight: "30px",
                    paddingLeft: "10px",
                    paddingRight: "10px",
                  }}
                  endIcon={<DocumentScannerRounded />}
                >
                  {loadingOnboardingRequest ? (
                    <CircularProgress
                      size={24}
                      sx={{ marginTop: "8px", color: "#CF0A2C" }}
                    />
                  ) : (
                    t("screens.fleetAndRoute.headerDriver.request")
                  )}
                </MButton>
              </Box>
            );
          }

          return null;


        },
      },
    ],
    [vehiclesList, driverDataList, loadingOnboardingRequest, loadingRows, t]
  );

  const columnDefsRoutes = useMemo(
    () => [
      {
        field: "countryFrom.name",
        headerName: t("screens.fleetAndRoute.tableHeadersRoutes.from"),
      },
      {
        field: "countryTo.name",
        headerName: t("screens.fleetAndRoute.tableHeadersRoutes.to"),
      },
      {
        field: "roundtrip",
        headerName: t("screens.fleetAndRoute.tableHeadersRoutes.type"),
        cellRenderer: ({ value }: any) => {
          return (
            <div>
              {value === true
                ? t("screens.fleetAndRoute.roundtrip")
                : t("screens.fleetAndRoute.oneWay")}
            </div>
          );
        },
      },
      {
        field: "createdOn",
        headerName: t("screens.fleetAndRoute.tableHeadersRoutes.added"),
        cellRenderer: ({ value }: any) => {
          return <div>{dayjs(value).format("DD/MM/YYYY")}</div>;
        },
      },
      {
        field: "action",
        headerName: t("screens.fleetAndRoute.tableHeadersRoutes.action"),
        cellRenderer: (props: any) => {
          return (
            <div
              onClick={deleteRoute(props.rowIndex)}
              className="flex items-center h-full cursor-pointer"
            >
              <div className="mr-1">
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M6 7H5V20C5 20.5304 5.21071 21.0391 5.58579 21.4142C5.96086 21.7893 6.46957 22 7 22H17C17.5304 22 18.0391 21.7893 18.4142 21.4142C18.7893 21.0391 19 20.5304 19 20V7H6ZM10 19H8V10H10V19ZM16 19H14V10H16V19ZM16.618 4L15 2H9L7.382 4H3V6H21V4H16.618Z"
                    className="fill-red"
                  />
                </svg>
              </div>

              <div>{t("screens.fleetAndRoute.deleteRoute")}</div>
            </div>
          );
        },
      },
    ],
    [routesComputed]
  );
  const { user } = useAuth();
  const localeText = {
    page: t("screens.trips.agGrid.page"),
    nextPage: t("screens.trips.agGrid.nextPage"),
    previousPage: t("screens.trips.agGrid.previousPage"),
    lastPage: t("screens.trips.agGrid.lastPage"),
    firstPage: t("screens.trips.agGrid.firstPage"),
    ariaPaginationPage: t("screens.trips.agGrid.ariaPaginationPage"),
    ariaPagination: t("screens.trips.agGrid.ariaPagination"),
    ariaLabelPagination: t("screens.trips.agGrid.ariaLabelPagination"),
    rangeSelectionEmpty: t("screens.trips.agGrid.rangeSelectionEmpty"),
    paginationSummary: t("screens.trips.agGrid.paginationSummary"),
    empty: t("screens.trips.agGrid.empty"),
    loadingOoo: t("screens.trips.agGrid.loadingOoo"),
    to: t("screens.trips.agGrid.to"),
    of: t("screens.trips.agGrid.of"),
  };

  const [statusLoader, setStatusLoader] = useState<boolean>(false);

  const navigate = useNavigate();

  const handleClick = (id: number) => {
    navigate(`/tripdetails/${id}`);
  };
  const [errorMessage, setErrorMessage] = useState<React.ReactNode>(null);
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);
  useEffect(() => {
  }, [errorMessage]);

  const [errorDialogOpen, setErrorDialogOpen] = useState(false);

  const handleStatusChange = async (phone: string, e?: React.MouseEvent<HTMLElement>) => {
    if (!businessProfile) return;
    try {
      setStatusLoader(true);
      // Trova il driver
      const driver = driverDataList.find((d: any) => d.phone === phone);
      if (!driver) {
        toast.error("Autista non trovato.");
        return;
      }
      // Determina il nuovo stato
      const newStatus: "ACTIVE" | "CANCELLED" =
        driver.status === "ACTIVE" ? "CANCELLED" : "ACTIVE";
      const editUser = user?.email ?? "";
      // Chiamata API per aggiornare lo stato
      await fleetService.updateDriverStatus(
        businessProfile.legacycodes[0].legacycode,
        businessProfile.legacycodes[0].company,
        phone,
        newStatus,
        editUser
      );
      // Se la chiamata ha successo, aggiorna lo stato locale
      setDriverDataList((prevDrivers: any) =>
        prevDrivers.map((d: any) =>
          d.phone === phone
            ? {
              ...d,
              status: newStatus,
              associations:
                newStatus === "CANCELLED"
                  ? d.associations.map((assoc: any) => ({
                    ...assoc,
                    status: "CANCELLED",
                  }))
                  : d.associations,
            }
            : d
        )
      );
      toast.success(`Stato aggiornato correttamente`);
    } catch (error: any) {
      console.error(
        "Errore nel cambio di stato della relazione autista-mezzo",
        error
      );
      let driver = driverDataList.find((d: any) => d.phone === phone);
      let errorMessage = (
        <>
          {error.response?.data?.tripInfo?.length > 0 && (
            <>
              <Typography sx={{ fontSize: "16px" }}>
                {t('screens.fleetAndRoute.errorDialog.driverRemoval', { name: driver.firstName + " " + driver.lastName })}
              </Typography>
              {error.response.data.tripInfo.map(
                (trip: { tripID: number; tmsObjectNumber: string }) => (
                  <Box
                    key={trip.tripID}
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "flex-start",
                      alignItems: "center",
                      columnGap: "4px",
                    }}
                  >
                    <Box
                      sx={{
                        width: "6px",
                        height: "6px",
                        backgroundColor: "#333333",
                        borderRadius: "100%",
                      }}
                    />
                    <MButton
                      onClick={() => handleClick(trip.tripID)}
                      variant="text"
                      color="primary"
                      style={{
                        textDecoration: "underline",
                        color: "#333333",
                        textTransform: "none",
                        fontSize: "16px",
                      }}
                    >
                      Trip ID: {trip.tmsObjectNumber}
                    </MButton>
                  </Box>
                )
              )}
            </>
          )}
        </>
      );
      setErrorMessage(errorMessage);
      setErrorDialogOpen(true);
    } finally {
      setStatusLoader(false);
    }
  };


  const [openDialog, setOpenDialog] = useState(false);
  const [openDialogDriverStatus, setOpenDialogDriverStatus] = useState(false);
  const [selectedDriverPhone, setSelectedDriverPhone] = useState<string | null>(
    null
  );
  const handleOpneDialogDriverStatus = (phone: string) => {
    setSelectedDriverPhone(phone);
    setOpenDialogDriverStatus(true);

  };
  const handleCloseDialogDriverStatus = () => {
    setOpenDialogDriverStatus(false);
    setSelectedDriverPhone(null);
  };
  const handleConfirmStatusDriverChange = async () => {
    if (selectedDriverPhone) {
      await handleStatusChange(selectedDriverPhone);
    }
    handleCloseDialogDriverStatus();
  };
  const [selectedLicensePlate, setSelectedLicensePlate] = useState<
    string | null
  >(null);

  const handleOpenDialog = (licensePlate: string) => {
    setSelectedLicensePlate(licensePlate);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedLicensePlate(null);
  };

  const handleConfirmStatusChange = async () => {
    if (!selectedLicensePlate) return;
    await handleStatusVehicleChange(selectedLicensePlate);
    handleCloseDialog();
  };
  const handleStatusVehicleChange = async (licensePlate: string) => {
    if (!businessProfile) return;
    try {
      setStatusLoader(true);
      // Trova il veicolo e determina il nuovo stato prima di aggiornare lo stato
      const vehicle = vehiclesList.find((v: any) => v.licensePlate === licensePlate);
      if (!vehicle) throw new Error("Veicolo non trovato");
      const newStatus: "ACTIVE" | "CANCELLED" = vehicle.status === "ACTIVE" ? "CANCELLED" : "ACTIVE";
      const editUser = user?.email || "";
      // Chiamata API per aggiornare lo stato del veicolo
      await fleetService.updateVehicleStatus(
        businessProfile.legacycodes[0].legacycode,
        businessProfile.legacycodes[0].company,
        licensePlate,
        newStatus,
        editUser
      );
      // Solo se la chiamata API ha successo, aggiorniamo lo stato
      setVehiclesList((prevVehicles: any) =>
        prevVehicles.map((v: any) =>
          v.licensePlate === licensePlate ? { ...v, status: newStatus } : v
        )
      );
      if (newStatus === "CANCELLED") {
        setDriverDataList((prevDrivers: any) =>
          prevDrivers.map((driver: any) => ({
            ...driver,
            associations: driver.associations.map((assoc: any) =>
              assoc.vehicleLicensePlate === licensePlate
                ? { ...assoc, status: newStatus }
                : assoc
            ),
          }))
        );
      }
      if (selectedVehicle?.vehicles) {
        setSelectedVehicle((prevSelectedVehicle: any) => ({
          ...prevSelectedVehicle,
          vehicles: prevSelectedVehicle.vehicles.map((v: any) =>
            v.licensePlate === licensePlate ? { ...v, status: newStatus } : v
          ),
        }));
      }
      toast.success("Stato veicolo aggiornato correttamente");
    } catch (error: any) {
      console.error(
        "Errore nel cambio di stato del veicolo",
        error
      );
      let vehicle = vehiclesList.find((v: any) => v.licensePlate === licensePlate);
      let errorMessage = (
        <>
          {error.response?.data?.tripInfo?.length > 0 && (
            <>
              <Typography sx={{ fontSize: "16px" }}>

                {t('screens.fleetAndRoute.errorDialog.vehicleRemoval', { plate: vehicle.licensePlate })}
              </Typography>
              {error.response.data.tripInfo.map(
                (trip: { tripID: number; tmsObjectNumber: string }) => (
                  <Box
                    key={trip.tripID}
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "flex-start",
                      alignItems: "center",
                      columnGap: "4px",
                    }}
                  >
                    <Box
                      sx={{
                        width: "6px",
                        height: "6px",
                        backgroundColor: "#333333",
                        borderRadius: "100%",
                      }}
                    />
                    <MButton
                      onClick={() => handleClick(trip.tripID)}
                      variant="text"
                      color="primary"
                      style={{
                        textDecoration: "underline",
                        color: "#333333",
                        textTransform: "none",
                        fontSize: "16px",
                      }}
                    >
                      Trip ID: {trip.tmsObjectNumber}
                    </MButton>
                  </Box>
                )
              )}
            </>
          )}
        </>
      );
      setErrorMessage(errorMessage);
      setErrorDialogOpen(true);
    } finally {
      setStatusLoader(false);
    }
  };


  const [emissionsData, setEmissionsData] = useState<EmissionsOutput[]>([]);
  const [fuelData, setFuelData] = useState<EmissionsOutput[]>([]);
  useEffect(() => {
    if (vehiclesList.length > 0) {
      setEmissionsData(extractEmissionsData(vehiclesList));
      setFuelData(extractFuelTypeData(vehiclesList));
    }
  }, [vehiclesList]);
 // Sincronizza selectedVehicle con vehiclesList quando i veicoli vengono aggiornati
 useEffect(() => {
  if (selectedVehicle && vehiclesList.length > 0) {
    // Se selectedVehicle è un singolo veicolo
    if (selectedVehicle.licensePlate) {
      const updatedVehicle = vehiclesList.find((v: any) => v.licensePlate === selectedVehicle.licensePlate);
      if (updatedVehicle && JSON.stringify(updatedVehicle) !== JSON.stringify(selectedVehicle)) {
        setSelectedVehicle(updatedVehicle);
      }
    }
    // Se selectedVehicle è un oggetto con array di veicoli (dal PopoverComponent)
    else if (selectedVehicle.vehicles && Array.isArray(selectedVehicle.vehicles)) {
      const updatedVehicles = selectedVehicle.vehicles.map((vehicle: any) => {
        const updatedVehicle = vehiclesList.find((v: any) => v.licensePlate === vehicle.licensePlate);
        return updatedVehicle || vehicle;
      });
      
      const hasChanges = updatedVehicles.some((vehicle: any, index: number) => 
        JSON.stringify(vehicle) !== JSON.stringify(selectedVehicle.vehicles[index])
      );
      
      if (hasChanges) {
        setSelectedVehicle((prev: any) => ({
          ...prev,
          vehicles: updatedVehicles
        }));
      }
    }
  }
}, [vehiclesList, selectedVehicle]);
  const windowWidth = useWindowSize();
  const isMobile = windowWidth < 700;

  return (
    <div className="p-6 md:p-8">
      <div className="md:flex items-start justify-between mb-6 md:mb-[14px]" >
        <h1 className="mb-4 md:mb-0 text-[32px] font-bold leading-[39px]">
          {t("screens.fleetAndRoute.heading")}
        </h1>

        {showedTab === "fleet" && isIntermodal === false && (
          <Button callback={openSidebarModifyFleet}>
            <div className="flex items-center">
              <span className="mr-[12px]">
                {t("common.buttonActions.fleet.modifyFleet")}
              </span>
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M10.8419 8.81317L7.61792 5.58922L8.08915 5.118L12.9712 10.0001L8.08915 14.8821L7.61792 14.4109L10.8419 11.1869L11.6954 10.3334H10.4883H3.83331V9.66672H10.4883H11.6954L10.8419 8.81317ZM15.5 5.50006H16.1666V14.5001H15.5V5.50006Z"
                  fill="white"
                  stroke="white"
                />
              </svg>
            </div>
          </Button>
        )}
        {/*DA RIMUOVERE DOPO PROD TECNICO* */}
        { 
          showedTab === "registeredAssets" && isIntermodal === false && (
            <Button callback={() => handleOpenSidebarAddVehicle()}>
              <div className="flex items-center">
                <span className="mr-[12px]">
                  {t("common.buttonActions.addVehicle")}
                </span>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M10.8419 8.81317L7.61792 5.58922L8.08915 5.118L12.9712 10.0001L8.08915 14.8821L7.61792 14.4109L10.8419 11.1869L11.6954 10.3334H10.4883H3.83331V9.66672H10.4883H11.6954L10.8419 8.81317ZM15.5 5.50006H16.1666V14.5001H15.5V5.50006Z"
                    fill="white"
                    stroke="white"
                  />
                </svg>
              </div>
            </Button>
          )}

        {showedTab === "routes" && (
          <Button callback={openSidebarAddRoute}>
            <div className="flex items-center">
              <span className="mr-[12px]">
                {t("common.buttonActions.routes.addRoutes")}
              </span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                className="w-[20px] h-[20px]"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 6v12m6-6H6"
                />
              </svg>
            </div>
          </Button>
        )}
        {/*DA RIMUOVERE DOPO PROD TECNICO* */}
        { 
          showedTab === "drivers" && isIntermodal === false && (
            <Button callback={() => setIsSidebarOpen(true)}>
              <div className="flex items-center">
                <span className="mr-[12px]">
                  {" "}
                  {t("common.sidebars.addDriver.heading")}{" "}
                </span>

                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M10.8419 8.81317L7.61792 5.58922L8.08915 5.118L12.9712 10.0001L8.08915 14.8821L7.61792 14.4109L10.8419 11.1869L11.6954 10.3334H10.4883H3.83331V9.66672H10.4883H11.6954L10.8419 8.81317ZM15.5 5.50006H16.1666V14.5001H15.5V5.50006Z"
                    fill="white"
                    stroke="white"
                  />
                </svg>
              </div>
            </Button>
          )}


      </div>

      {/* TABS */}

      <div >
        <div className="flex justify-between">
          <div style={{ width: '100%', display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '30px' }}>
            <div className="flex items-center ">

              {/*DA RIMUOVERE DOPO PROD TECNICO* */}
              {
                <div
                  onClick={() => setShowedTab("registeredAssets")}
                  className={
                    "mr-4 rounded-[30px] py-1 px-4 uppercase font-semibold text-[12px] md:text-sm leading-[17px] cursor-pointer" +
                    (showedTab === "registeredAssets"
                      ? " bg-red text-white"
                      : " bg-grey")
                  }
                >
                  {t("screens.fleetAndRoute.registeredAssets")}
                </div>
              }

              {/*DA RIMUOVERE DOPO PROD TECNICO* */}
              {
                <div
                  onClick={() => setShowedTab("drivers")}
                  className={
                    "mr-4 rounded-[30px] py-1 px-4 uppercase font-semibold text-[12px] md:text-sm leading-[17px] cursor-pointer" +
                    (showedTab === "drivers" ? " bg-red text-white" : " bg-grey")
                  }
                >
                  {t("screens.fleetAndRoute.drivers")}
                </div>
              }

              <div
                onClick={() => setShowedTab("fleet")}
                className={
                  "mr-4 rounded-[30px] py-1 px-4 uppercase font-semibold text-[12px] md:text-sm leading-[17px] cursor-pointer" +
                  (showedTab === "fleet" ? " bg-red text-white" : " bg-grey")
                }
              >
                {t("screens.fleetAndRoute.fleet")}
              </div>

              <div
                onClick={() => setShowedTab("routes")}
                className={
                  "mr-4 rounded-[30px] py-1 px-4 uppercase font-semibold text-[12px] md:text-sm leading-[17px] cursor-pointer" +
                  (showedTab === "routes" ? " bg-red text-white" : " bg-grey")
                }
              >
                {t("screens.fleetAndRoute.routes")}
              </div>
            </div>
          </div>



          {lastUpdate !== null &&
            showedTab === "fleet" &&
            isIntermodal === false && (
              <div className="text-xs" style={{ whiteSpace: 'nowrap' }}>
                Last update {dayjs(lastUpdate).format("DD/MM/YYYY")}
              </div>
            )}
        </div>

        {showedTab === "drivers" && (
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            width: "max-content",
            border: "solid 1px",
            borderRadius: "5px",
            borderColor: "red",
            padding: "8px"
          }}>
            <Typography
              sx={{
                fontSize: "14px",
                color: "#333333",
                textTransform: "uppercase",
              }}
            >
              {t("screens.fleetAndRoute.headerDriver.partitaIva") + ":"}
            </Typography>
            <Typography sx={{ fontSize: "14px", textAlign: 'right', color: "#333333", fontWeight: "bold", }}>
              {("FTL." + businessProfile?.vatnr) || ""}
            </Typography>
          </Box>
        )}

        <div
          className={
            "flex items-center justify-between" +
            (showedTab === "routes" ? " mb-0 md:mb-[26px]" : " mb-[26px]")
          }
        >
          {/* Grid or Table buttons */}
          {showedTab === "drivers" ? (
            ""
          ) : (
            <div className="hidden md:flex items-center">
              <div
                className={
                  "mr-[10px] cursor-pointer h-6 w-6 rounded-[4px] flex items-center justify-center" +
                  (visualizationType === "grid" ? " bg-red" : "")
                }
                onClick={() => setVisualizationType("grid")}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M0 0H6V6H0V0ZM8 0H14V6H8V0ZM0 8H6V14H0V8Z"
                    fill={visualizationType === "grid" ? "#FFFFFF" : "#6F6F6F"}
                  />
                  <path
                    d="M14 8H8V14H14V8Z"
                    fill={visualizationType === "grid" ? "#FFFFFF" : "#6F6F6F"}
                  />
                </svg>
              </div>

              <div
                className={
                  "cursor-pointer h-6 w-6 rounded-[4px] flex items-center justify-center" +
                  (visualizationType === "table" ? " bg-red" : "")
                }
                onClick={() => setVisualizationType("table")}
              >
                <svg
                  width="16"
                  height="10"
                  viewBox="0 0 16 10"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M0 0H16V2H0V0ZM0 4H16V6H0V4ZM0 8H16V10H0V8Z"
                    fill={visualizationType === "table" ? "#FFFFFF" : "#6F6F6F"}
                  />
                </svg>
              </div>
            </div>
          )}
          {/* FILTERS */}
          {showedTab === "fleet" && (
            <div
              ref={filterDropdownRef}
              onClick={openFilters}
              className="relative flex items-center cursor-pointer select-none"
            >
              <div className="mr-[10px]">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M20 3H4C3.73478 3 3.48043 3.10536 3.29289 3.29289C3.10536 3.48043 3 3.73478 3 4V6.59C3 7.113 3.213 7.627 3.583 7.997L9 13.414V21C9.0002 21.1704 9.04387 21.3379 9.1269 21.4867C9.20992 21.6354 9.32955 21.7605 9.47444 21.8502C9.61934 21.9398 9.78471 21.9909 9.9549 21.9988C10.1251 22.0066 10.2945 21.9709 10.447 21.895L14.447 19.895C14.786 19.725 15 19.379 15 19V13.414L20.417 7.997C20.787 7.627 21 7.113 21 6.59V4C21 3.73478 20.8946 3.48043 20.7071 3.29289C20.5196 3.10536 20.2652 3 20 3Z"
                    fill="#CF0A2C"
                  />
                </svg>
              </div>
              <div className="uppercase font-semibold text-sm leading-[17px]">
                {currentFilter === null && (
                  <span>{t("screens.fleetAndRoute.filter")}</span>
                )}
                {currentFilter !== null && (
                  <span className="uppercase font-semibold text-sm leading-[17px]">
                    <span className="text-red">
                      {t("screens.fleetAndRoute.filter")}:
                    </span>
                    <span className="ml-1">{currentFilter}</span>
                  </span>
                )}
              </div>

              {isOpenFilters && (
                <div className="absolute z-10 w-max right-[100%] translate-x-[100%] md:right-0 md:translate-x-0 top-[calc(100%+10px)] bg-white border border-red rounded-[8px] p-4">
                  <div
                    onClick={changeFilter(null)}
                    className={
                      "text-sm leading-[17px] mb-4 flex items-center" +
                      (currentFilter === null ? " font-bold" : "")
                    }
                  >
                    {currentFilter === null && (
                      <span className="block h-[6px] w-[6px] rounded-full bg-red mr-1"></span>
                    )}
                    <span>{t("screens.fleetAndRoute.allVehicles")}</span>
                  </div>
                  {vehiclesComputed.map((vehicle, j) => {
                    return (
                      <div
                        onClick={changeFilter(vehicle.type)}
                        key={j}
                        className={
                          "text-sm leading-[17px] flex items-center" +
                          (j === vehiclesComputed.length - 1 ? "" : " mb-4") +
                          (currentFilter === vehicle.type ? " font-bold" : "")
                        }
                      >
                        {currentFilter === vehicle.type && (
                          <span className="block h-[6px] w-[6px] rounded-full bg-red mr-1"></span>
                        )}
                        <span>{vehicle.type}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {showedTab === "fleet" && (
          <div className="grid grid-cols-12 gap-6">
            {visualizationType === "grid" && (
              <>
                {unGroupVehicles.length > 0 && (
                  <div className="col-span-12 md:col-span-6">
                    {unGroupVehicles.map((vehicle, index) => {
                      return (
                        <div
                          key={index}
                          className={
                            "bg-lightGrey shadow-card p-[20px] flex items-center justify-between" +
                            (index !== unGroupVehicles.length - 1
                              ? " mb-6"
                              : "")
                          }
                        >
                          <div>
                            <div className="uppercase text-red leading-[17px] text-sm font-semibold mb-2">
                              {vehicle.type}
                            </div>
                            <div className="text-[18px] font-bold leading-[21px]">
                              {vehicle.name}
                            </div>
                          </div>
                          <div className="flex items-center">
                            <div className="text-2xl text-red font-bold leading-[29px] mr-4">
                              {vehicle.quantity}
                            </div>
                            <div
                              dangerouslySetInnerHTML={{ __html: vehicle.icon }}
                            ></div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {unGroupVehicles.length === 0 && (
                  <div className="col-span-12 md:col-span-6">
                    {t("screens.fleetAndRoute.emptyVehicles")}
                  </div>
                )}
              </>
            )}

            {visualizationType === "table" && (
              <div className="col-span-12 md:col-span-6">
                <div
                  className="ag-theme-alpine"
                  style={{ width: "100%", height: "100%" }}
                >
                  <AgGridReact
                    ref={gridRef}
                    rowData={unGroupVehicles} // Row Data for Rows
                    columnDefs={columnDefs} // Column Defs for Columns
                    defaultColDef={{
                      flex: 1,
                      minWidth: 100,
                    }}
                    onGridReady={onGridReady}
                    suppressRowClickSelection
                    suppressCellFocus
                    suppressMovableColumns
                  />
                </div>
              </div>
            )}

            <div className="col-span-12 md:col-span-6">
              <div className="grid grid-cols-12 md:grid-cols-6 lg:grid-cols-12 gap-6">
                <Suspense
                  fallback={<div>{t("screens.fleetAndRoute.loading")}</div>}
                >
                  <FleetGraphs bgColor="bg-lightGrey shadow-card" />
                </Suspense>
              </div>
            </div>
          </div>
        )}
        {showedTab === "registeredAssets" && (
          <div style={{ display: "flex", columnGap: "24px", flexDirection: isMobile ? 'column' : 'row' }}>
            {visualizationType === "grid" && (
              <>
                <VehiclesGrid handleOpen={handleOpen} vehicles={vehiclesList} />

                <PopoverComponent
                  handleOpenViewSidebar={handleOpenViewSidebar}
                  open={open}
                  vehiclesGridData={selectedVehicle}
                  onClose={handleClose}
                  handleStatusVehicleChange={handleStatusVehicleChange}
                />
              </>
            )}

            {visualizationType === "table" && (
              <div style={{ width: "100%" }}>
                <div
                  className="ag-theme-alpine"
                  style={{ height: "100%", width: "100%", overflowX: "auto" }}
                >
                  <AgGridReact
                    ref={gridRef}
                    rowData={vehiclesList} // Row Data for Rows
                    columnDefs={columnDefsRegistered} // Column Defs for Columns
                    pagination={true}
                    paginationPageSize={paginationPageSize}
                    defaultColDef={{
                      minWidth: 80,
                      resizable: true,
                      filter: true,
                      floatingFilter: true,
                      suppressFiltersToolPanel: false,
                      sortable: true,
                      flex: 1,
                    }}
                    onGridReady={onGridReady}
                    domLayout="autoHeight"
                    localeText={localeText}
                    enableBrowserTooltips={true}
                    suppressDragLeaveHidesColumns={true}
                    enableCellTextSelection={true}  // ✅ Selezione del testo nella cella
                    suppressClipboardApi={true}
                  />
                  <div style={{ width: "100%", height: "4px" }}>
                    <PaginationSizeSelector
                      currentPageSize={paginationPageSize}
                      onPageSizeChange={handlePageSizeChange}
                    />
                  </div>
                </div>
              </div>
            )}

            {visualizationType === "grid" && (
              <div style={{ width: "100%" }}>
                <div className="grid grid-cols-12 md:grid-cols-6 lg:grid-cols-12 gap-6">
                  <Suspense
                    fallback={<div>{t("screens.fleetAndRoute.loading")}</div>}
                  >
                    <FleetGraphsRegistered
                      engineType={fuelData}
                      emissionClass={emissionsData}
                    />
                  </Suspense>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ROUTES */}
        {showedTab === "routes" && (
          <>
            {/* Modal delete route */}
            {isOpenModalDeleteRoute === true &&
              !!routeToDelete &&
              arrayRoutesToDelete.length > 0 && (
                <div className="fixed top-0 left-0 z-10 h-[100dvh] w-screen bg-[#00000080] flex items-center justify-center">
                  <div className="bg-white h-[65%] w-[95%] lg:w-[50%] max-w-[700px] flex flex-col">
                    <div className="shrink-0 flex justify-between border-b p-[30px] items-center">
                      <div className="font-bold text-lg">
                        {t("screens.fleetAndRoute.deleteRoute")}
                      </div>

                      <div
                        onClick={() => setIsOpenModalDeleteRoute(false)}
                        className="cursor-pointer"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={2}
                          stroke="currentColor"
                          className="w-8 h-8"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </div>
                    </div>

                    {/* MAIN CONTENT */}
                    <div className="grow py-8 px-[30px] select-none overflow-auto relative">
                      {arrayRoutesToDelete.length > 1 && (
                        <>
                          <p className="mb-5">
                            {t("screens.fleetAndRoute.modalDelete.choseText")}
                          </p>
                          <div>
                            {arrayRoutesToDelete.map((route: IRoute, index) => {
                              return (
                                <div
                                  key={index}
                                  className="mb-2 flex items-center"
                                >
                                  <input
                                    onChange={() =>
                                      handleChangeDeleteRouteCheckbox(route.id)
                                    }
                                    id={route.id}
                                    type="checkbox"
                                    value={route.id}
                                  />
                                  <label
                                    className="mb-[3px] ml-2"
                                    htmlFor={route.id}
                                    dangerouslySetInnerHTML={{
                                      __html: t(
                                        "screens.fleetAndRoute.modalDelete.text",
                                        {
                                          country_from_name:
                                            route?.countryFrom.name,
                                          country_to_name:
                                            route?.countryTo.name,
                                        }
                                      ).toString(),
                                    }}
                                  ></label>
                                </div>
                              );
                            })}
                          </div>
                        </>
                      )}

                      {arrayRoutesToDelete.length === 1 &&
                        routesToDelete.length === 1 && (
                          <>
                            <p
                              className="mb-5"
                              dangerouslySetInnerHTML={{
                                __html: t(
                                  "screens.fleetAndRoute.modalDelete.textSingleRoute",
                                  {
                                    country_from_name:
                                      routeToDelete?.countryFrom.name,
                                    country_to_name:
                                      routeToDelete?.countryTo.name,
                                  }
                                ).toString(),
                              }}
                            ></p>

                            <p>
                              {t(
                                "screens.fleetAndRoute.modalDelete.confirmText"
                              )}
                            </p>
                          </>
                        )}
                    </div>

                    <div className="select-none px-[30px] py-6 border-t flex items-center justify-end">
                      <Button
                        callback={confirmDeleteRoute}
                        loading={isLoadingDelete}
                      >
                        {t("common.sidebars.saveButtonText")}
                      </Button>
                    </div>
                  </div>
                </div>
              )}

            <World ref={worldRef} />

            {visualizationType === "grid" && (
              <div className="grid grid-cols-12 gap-6">
                {routesComputed.length > 0 && (
                  <>
                    {routesComputed.map((route, index) => {
                      return (
                        <div
                          key={index}
                          className="select-none col-span-12 md:col-span-6 lg:col-span-4 xl:col-span-3 md:flex md:flex-col md:justify-between rounded-[8px] bg-lightGrey shadow-card p-4"
                        >
                          <div className="text-right text-[13px] mb-2 leading-[16px]">
                            <div className="mb-[6px]">
                              {t("screens.fleetAndRoute.added")}{" "}
                              <strong>
                                {dayjs(route.createdOn).format("DD/MM/YYYY")}
                              </strong>
                            </div>
                          </div>

                          <div className="relative mb-1">
                            {/* FROM */}
                            <div className="flex justify-between items-center mb-1">
                              <div>
                                <h4 className="text-red uppercase font-semibold leading-[17px] text-sm mb-2">
                                  {t("screens.fleetAndRoute.from")}
                                </h4>
                                <h5 className="text-[18px] font-bold leading-[21px] mb-2">
                                  {route.countryFrom.name}
                                </h5>
                              </div>

                              {
                                <div className="pr-6">
                                  <CountryMapSvg
                                    countryName={route.countryFrom.name}
                                    isoCode={route.countryFrom.isoCode}
                                    worldRef={worldRef}
                                  />
                                </div>
                              }
                            </div>

                            <div className="absolute right-0 top-[50%] -translate-y-[50%]">
                              {route.roundtrip === true && (
                                <svg
                                  width="24"
                                  height="72"
                                  viewBox="0 0 24 72"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M12.5 21L12.5 6C12.5 3.23858 10.2614 1 7.5 1H7"
                                    stroke="#6F6F6F"
                                    strokeDasharray="2 2"
                                  />
                                  <g clipPath="url(#clip0_12328_5047)">
                                    <path
                                      d="M17 39V41C17 42.3261 16.4732 43.5979 15.5355 44.5355C14.5978 45.4732 13.3261 46 12 46C10.6739 46 9.40213 45.4732 8.46445 44.5355C7.52676 43.5979 6.99998 42.3261 6.99998 41H9.49998C9.59361 41.0001 9.68539 40.9738 9.76484 40.9243C9.8443 40.8748 9.90825 40.8039 9.94942 40.7198C9.99058 40.6357 10.0073 40.5417 9.99768 40.4486C9.98806 40.3555 9.95247 40.2669 9.89498 40.193L6.39498 35.693C6.34824 35.6328 6.28836 35.584 6.21989 35.5505C6.15143 35.517 6.07621 35.4996 5.99998 35.4996C5.92375 35.4996 5.84853 35.517 5.78007 35.5505C5.71161 35.584 5.65172 35.6328 5.60498 35.693L2.10498 40.193C2.04749 40.2669 2.01191 40.3555 2.00228 40.4486C1.99266 40.5417 2.00938 40.6357 2.05054 40.7198C2.09171 40.8039 2.15566 40.8748 2.23512 40.9243C2.31458 40.9738 2.40635 41.0001 2.49998 41H4.99998C4.99998 42.8565 5.73748 44.637 7.05023 45.9497C8.36299 47.2625 10.1435 48 12 48C13.8565 48 15.637 47.2625 16.9497 45.9497C18.2625 44.637 19 42.8565 19 41V39C19 38.7348 18.8946 38.4804 18.7071 38.2929C18.5196 38.1054 18.2652 38 18 38C17.7348 38 17.4804 38.1054 17.2929 38.2929C17.1053 38.4804 17 38.7348 17 39Z"
                                      fill="#CF0A2C"
                                    />
                                    <path
                                      d="M5 31V33C5 33.2653 5.10536 33.5196 5.29289 33.7071C5.48043 33.8947 5.73478 34 6 34C6.26522 34 6.51957 33.8947 6.70711 33.7071C6.89464 33.5196 7 33.2653 7 33V31C7 29.674 7.52678 28.4022 8.46447 27.4645C9.40215 26.5268 10.6739 26 12 26C13.3261 26 14.5979 26.5268 15.5355 27.4645C16.4732 28.4022 17 29.674 17 31H14.5C14.4064 31 14.3146 31.0262 14.2351 31.0757C14.1557 31.1253 14.0917 31.1961 14.0506 31.2802C14.0094 31.3643 13.9927 31.4583 14.0023 31.5514C14.0119 31.6446 14.0475 31.7331 14.105 31.807L17.605 36.307C17.6517 36.3673 17.7116 36.416 17.7801 36.4495C17.8485 36.4831 17.9238 36.5005 18 36.5005C18.0762 36.5005 18.1515 36.4831 18.2199 36.4495C18.2884 36.416 18.3483 36.3673 18.395 36.307L21.895 31.807C21.9525 31.7331 21.9881 31.6446 21.9977 31.5514C22.0073 31.4583 21.9906 31.3643 21.9494 31.2802C21.9083 31.1961 21.8443 31.1253 21.7649 31.0757C21.6854 31.0262 21.5936 31 21.5 31H19C19 29.1435 18.2625 27.363 16.9497 26.0503C15.637 24.7375 13.8565 24 12 24C10.1435 24 8.36301 24.7375 7.05025 26.0503C5.7375 27.363 5 29.1435 5 31Z"
                                      fill="#CF0A2C"
                                    />
                                  </g>
                                  <path
                                    d="M12.5 51L12.5 66C12.5 68.7614 10.2614 71 7.5 71H7"
                                    stroke="#6F6F6F"
                                    strokeDasharray="2 2"
                                  />
                                  <defs>
                                    <clipPath id="clip0_12328_5047">
                                      <rect
                                        width="24"
                                        height="24"
                                        fill="white"
                                        transform="translate(0 24)"
                                      />
                                    </clipPath>
                                  </defs>
                                </svg>
                              )}

                              {route.roundtrip === false && (
                                <svg
                                  width="24"
                                  height="72"
                                  viewBox="0 0 24 72"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M12.5 21L12.5 6C12.5 3.23858 10.2614 1 7.5 1H7"
                                    stroke="#6F6F6F"
                                    strokeDasharray="2 2"
                                  />
                                  <path
                                    d="M7.50082 39C7.40404 38.9998 7.30929 39.0278 7.22807 39.0804C7.14686 39.133 7.08266 39.2081 7.04328 39.2965C7.00389 39.3849 6.99101 39.4829 7.00619 39.5784C7.02138 39.674 7.06398 39.7632 7.12882 39.835L11.6288 44.835C11.6757 44.8872 11.7331 44.929 11.7972 44.9576C11.8613 44.9861 11.9306 45.0009 12.0008 45.0009C12.071 45.0009 12.1404 44.9861 12.2045 44.9576C12.2686 44.929 12.3259 44.8872 12.3728 44.835L16.8728 39.835C16.9377 39.7632 16.9803 39.674 16.9954 39.5784C17.0106 39.4829 16.9978 39.3849 16.9584 39.2965C16.919 39.2081 16.8548 39.133 16.7736 39.0804C16.6923 39.0278 16.5976 38.9998 16.5008 39H13.0008V28C13.0008 27.7348 12.8955 27.4804 12.7079 27.2929C12.5204 27.1054 12.266 27 12.0008 27C11.7356 27 11.4812 27.1054 11.2937 27.2929C11.1062 27.4804 11.0008 27.7348 11.0008 28V39H7.50082Z"
                                    fill="#CF0A2C"
                                  />
                                  <path
                                    d="M12.5 51L12.5 66C12.5 68.7614 10.2614 71 7.5 71H7"
                                    stroke="#6F6F6F"
                                    strokeDasharray="2 2"
                                  />
                                </svg>
                              )}
                            </div>

                            {/* TO */}
                            <div className="flex justify-between items-center mb-1">
                              <div>
                                <h4 className="text-red uppercase font-semibold leading-[17px] text-sm mb-2">
                                  {t("screens.fleetAndRoute.to")}
                                </h4>
                                <h5 className="text-[18px] font-bold leading-[21px] mb-2">
                                  {route.countryTo.name}
                                </h5>
                              </div>

                              {
                                <div className="pr-6">
                                  <CountryMapSvg
                                    countryName={route.countryTo.name}
                                    isoCode={route.countryTo.isoCode}
                                    worldRef={worldRef}
                                  />
                                </div>
                              }
                            </div>
                          </div>

                          <div
                            onClick={deleteRoute(index)}
                            className="flex items-center justify-center bg-red h-9 w-9 rounded-[8px] cursor-pointer"
                          >
                            <svg
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M6 7H5V20C5 20.5304 5.21071 21.0391 5.58579 21.4142C5.96086 21.7893 6.46957 22 7 22H17C17.5304 22 18.0391 21.7893 18.4142 21.4142C18.7893 21.0391 19 20.5304 19 20V7H6ZM10 19H8V10H10V19ZM16 19H14V10H16V19ZM16.618 4L15 2H9L7.382 4H3V6H21V4H16.618Z"
                                fill="white"
                              />
                            </svg>
                          </div>
                        </div>
                      );
                    })}
                  </>
                )}

                {routesComputed.length === 0 && (
                  <div className="col-span-12">
                    {t("screens.fleetAndRoute.emptyRoutes")}
                  </div>
                )}
              </div>
            )}

            {visualizationType === "table" && (
              <>
                <div
                  className="ag-theme-alpine"
                  style={{ width: "100%", height: "100%" }}
                >
                  <AgGridReact
                    ref={gridRef}
                    rowData={routesComputed} // Row Data for Rows
                    columnDefs={columnDefsRoutes} // Column Defs for Columns
                    defaultColDef={{
                      flex: 1,
                      minWidth: 100,
                    }}
                    onGridReady={onGridReady}
                    suppressRowClickSelection
                    suppressCellFocus
                    suppressMovableColumns
                  />
                </div>
              </>
            )}
          </>
        )}

        {/* DRIVERS*/}
        {showedTab === "drivers" && (
          <div style={{ display: "flex", columnGap: "24px" }}>
            <div style={{ width: "100%" }}>
              <div
                className="ag-theme-alpine"
                style={{ height: "100%", width: "100%", overflowX: "auto" }}
              >
                <AgGridReact
                  ref={gridRef}
                  rowData={driverDataList} // Row Data for Rows
                  columnDefs={columnDefsDrivers} // Column Defs for Columns
                  pagination={true}
                  paginationPageSize={paginationPageSize}

                  defaultColDef={{
                    minWidth: 80,
                    resizable: true,
                    filter: true,
                    floatingFilter: true,
                    suppressFiltersToolPanel: false,
                    sortable: true,
                    flex: 1,
                  }}
                  onGridReady={(params) => onGridReady(params)}
                  onFilterChanged={onFilterChanged}
                  domLayout="autoHeight"
                  localeText={localeText}
                  suppressDragLeaveHidesColumns={true}
                  enableBrowserTooltips={true}
                  enableCellTextSelection={true}  // ✅ Selezione del testo nella cella
                  suppressClipboardApi={true}
                  frameworkComponents={{
                    DriverPlateFilter: DriverPlateFilter,
                    agCustomFilter: AssociationPeriodFilter
                  }}
                  suppressPropertyNamesCheck={true}
                />
                <div style={{ width: "100%", height: "4px" }}>
                  <PaginationSizeSelector
                    currentPageSize={paginationPageSize}
                    onPageSizeChange={handlePageSizeChange}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <SidebarAddDriver
        open={isSidebarOpen}
        onClose={handleCloseViewSidebarAddDriver}
        onAddDriver={handleAddDriver}
      />
      <SidebarViewVehicle
        open={isSidebarViewOpen}
        onClose={handleCloseViewSidebar}
        onAddVehicle={handleAddVehicle}
        selectedVehicle={selectedVehicle}
        fleetInfo={fleetInfo}
        onEdit={handleOpenSidebarAddVehicle}
      />
      <SidebarAddVehicle
        open={isSidebarAddVehicleOpen}
        onClose={handleCloseSidebarAddVehicle}
        onAddVehicle={handleAddVehicle}
        selectedVehicle={selectedVehicle}
        fleetInfo={fleetInfo}
        handleOpenDialog={handleOpenDialog}
      />

      <Dialog
        sx={{ zIndex: 9999 }}
        open={openDialog}
        onClose={handleCloseDialog}
      >
        <DialogTitle>{t("screens.fleetAndRoute.handleConfirm.title")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("screens.fleetAndRoute.handleConfirm.text1")}
            <strong>{selectedLicensePlate}</strong>?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <MButton
            variant="outlined"
            onClick={handleCloseDialog}
            sx={{ color: "#333333", borderColor: "#333333" }}
          >
            {t("screens.fleetAndRoute.headerDriver.cancel")}
          </MButton>
          <MButton
            variant="contained"
            onClick={handleConfirmStatusChange}
            sx={{ backgroundColor: "#CF0A2C" }}
            autoFocus
          >
            {t("screens.fleetAndRoute.headerDriver.save")}
          </MButton>
        </DialogActions>
      </Dialog>

      <Dialog
        sx={{ zIndex: 9999 }}
        open={openDialogDriverStatus}
        onClose={handleCloseDialogDriverStatus}
      >
        <DialogTitle>{t("screens.fleetAndRoute.handleConfirm.title")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("screens.fleetAndRoute.handleConfirm.text")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <MButton
            variant="outlined"
            onClick={handleCloseDialogDriverStatus}
            sx={{ color: "#333333", borderColor: "#333333" }}
          >
            {t("screens.fleetAndRoute.headerDriver.cancel")}
          </MButton>
          <MButton
            variant="contained"
            onClick={handleConfirmStatusDriverChange}
            sx={{ backgroundColor: "#CF0A2C" }}
            autoFocus
          >
            {t("screens.fleetAndRoute.headerDriver.save")}
          </MButton>
        </DialogActions>
      </Dialog>
      {errorMessage === true &&
        <CustomPopover open={Boolean(popoverAnchor)}
          anchorEl={popoverAnchor}
          onClose={() => setPopoverAnchor(null)}>{errorMessage}</CustomPopover>
      }
      <Dialog
        open={errorDialogOpen}
        onClose={() => setErrorDialogOpen(false)}
        fullWidth
        PaperProps={{
          sx: {
            border: "2px solid #CF0A2C",
            borderRadius: "10px",
            maxWidth: "350px",
            mx: "auto", // centra orizzontalmente
            padding: 1
          }
        }}
        BackdropProps={{
          sx: {
            backgroundColor: "transparent"
          }
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: '10px' }}>
          <Typography variant="h6" style={{ color: "#CF0A2C", fontWeight: "bold" }}>
            {t('screens.fleetAndRoute.errorDialog.noteTitle')}
          </Typography>
          <IconButton
            onClick={() => setErrorDialogOpen(false)}
            style={{
              color: "#333333",
              padding: 0
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ padding: 0 }} >
          {errorMessage}
        </DialogContent>

      </Dialog>
    </div>
  );
};

export default FleetAndRoute;
